import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  archiveFeatureFlag,
  clearFlagGate,
  createFeatureFlag,
  createFlagRule,
  deleteFlagRule,
  fetchFeatureFlags,
  fetchFlagGates,
  fetchFlagRules,
  updateFeatureFlag,
  updateFlagRule,
  upsertFlagGate,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import { TENANT_FEATURE_FLAGS_QUERY_KEY } from '@/hooks/use-tenant-feature-flags';
import type {
  ArchiveFeatureFlagResponse,
  ClearFlagGateResponse,
  CreateFeatureFlagBody,
  DeleteFlagRuleResponse,
  FeatureFlagDefinition,
  FeatureFlagGatePlatform,
  FeatureFlagsResponse,
  FlagGatesResponse,
  FlagRuleBody,
  FlagRuleResponse,
  FlagRulesResponse,
  FlagScope,
  UpdateFlagRuleBody,
  UpdateFeatureFlagBody,
  UpdateFeatureFlagResponse,
  UpsertFlagGateBody,
  UpsertFlagGateResponse,
} from '@/types/feature-flag';

const FEATURE_FLAGS_QUERY_KEY = ['featureFlags'] as const;

function useToken(): () => string {
  const { getIdToken } = useAuth();
  return () => {
    const token = getIdToken();
    if (!token) throw new Error('No ID token available. Please log in again.');
    return token;
  };
}

function scopedListKey(scope: FlagScope): readonly string[] {
  // Imported rather than restated: a renamed key would otherwise leave create
  // and archive invalidating a cache nobody reads.
  return scope === 'tenant' ? TENANT_FEATURE_FLAGS_QUERY_KEY : FEATURE_FLAGS_QUERY_KEY;
}

function gatesQueryKey(scope: FlagScope, key: string): readonly string[] {
  return ['featureFlagGates', scope, key] as const;
}

function rulesQueryKey(scope: FlagScope, key: string): readonly string[] {
  return ['featureFlagRules', scope, key] as const;
}

/** A rule write moves `ruleCount` on the flag list as well as the rule list,
 * so both caches go stale together. */
function invalidateRules(
  queryClient: ReturnType<typeof useQueryClient>,
  scope: FlagScope,
  key: string
): void {
  void queryClient.invalidateQueries({ queryKey: rulesQueryKey(scope, key) });
  void queryClient.invalidateQueries({ queryKey: scopedListKey(scope) });
}

export function useFeatureFlags(): UseQueryResult<FeatureFlagsResponse> {
  const requireToken = useToken();

  return useQuery({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn: async () => fetchFeatureFlags(requireToken()),
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useUpdateFeatureFlag(): UseMutationResult<
  UpdateFeatureFlagResponse,
  Error,
  { key: string; body: UpdateFeatureFlagBody }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, body }) => updateFeatureFlag(key, body, requireToken()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
    },
  });
}

export function useCreateFeatureFlag(): UseMutationResult<
  FeatureFlagDefinition,
  Error,
  { scope: FlagScope; body: CreateFeatureFlagBody }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scope, body }) => createFeatureFlag(scope, body, requireToken()),
    onSuccess: (_definition, { scope }) => {
      void queryClient.invalidateQueries({ queryKey: scopedListKey(scope) });
    },
  });
}

export function useArchiveFeatureFlag(): UseMutationResult<
  ArchiveFeatureFlagResponse,
  Error,
  { scope: FlagScope; key: string }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scope, key }) => archiveFeatureFlag(scope, key, requireToken()),
    onSuccess: (_result, { scope }) => {
      void queryClient.invalidateQueries({ queryKey: scopedListKey(scope) });
    },
  });
}

export function useFlagGates(
  scope: FlagScope,
  key: string,
  enabled: boolean
): UseQueryResult<FlagGatesResponse> {
  const requireToken = useToken();

  return useQuery({
    queryKey: gatesQueryKey(scope, key),
    queryFn: async () => fetchFlagGates(scope, key, requireToken()),
    enabled: enabled && key.length > 0,
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 2,
  });
}

export function useUpsertFlagGate(): UseMutationResult<
  UpsertFlagGateResponse,
  Error,
  { scope: FlagScope; key: string; platform: FeatureFlagGatePlatform; body: UpsertFlagGateBody }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scope, key, platform, body }) =>
      upsertFlagGate(scope, key, platform, body, requireToken()),
    onSuccess: (_result, { scope, key }) => {
      void queryClient.invalidateQueries({ queryKey: gatesQueryKey(scope, key) });
    },
  });
}

export function useClearFlagGate(): UseMutationResult<
  ClearFlagGateResponse,
  Error,
  { scope: FlagScope; key: string; platform: FeatureFlagGatePlatform }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scope, key, platform }) =>
      clearFlagGate(scope, key, platform, requireToken()),
    onSuccess: (_result, { scope, key }) => {
      void queryClient.invalidateQueries({ queryKey: gatesQueryKey(scope, key) });
    },
  });
}

export function useFlagRules(
  scope: FlagScope,
  key: string,
  enabled: boolean
): UseQueryResult<FlagRulesResponse> {
  const requireToken = useToken();

  return useQuery({
    queryKey: rulesQueryKey(scope, key),
    queryFn: async () => fetchFlagRules(scope, key, requireToken()),
    enabled: enabled && key.length > 0,
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 2,
  });
}

export function useCreateFlagRule(): UseMutationResult<
  FlagRuleResponse,
  Error,
  { scope: FlagScope; key: string; body: FlagRuleBody }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scope, key, body }) => createFlagRule(scope, key, body, requireToken()),
    onSuccess: (_result, { scope, key }) => invalidateRules(queryClient, scope, key),
  });
}

export function useUpdateFlagRule(): UseMutationResult<
  FlagRuleResponse,
  Error,
  { scope: FlagScope; key: string; ruleId: string; body: UpdateFlagRuleBody }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scope, key, ruleId, body }) =>
      updateFlagRule(scope, key, ruleId, body, requireToken()),
    onSuccess: (_result, { scope, key }) => invalidateRules(queryClient, scope, key),
  });
}

export function useDeleteFlagRule(): UseMutationResult<
  DeleteFlagRuleResponse,
  Error,
  { scope: FlagScope; key: string; ruleId: string }
> {
  const requireToken = useToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scope, key, ruleId }) =>
      deleteFlagRule(scope, key, ruleId, requireToken()),
    onSuccess: (_result, { scope, key }) => invalidateRules(queryClient, scope, key),
  });
}
